import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Link, useParams } from "react-router-dom";
import { AccountPageWrapper } from "../AccountPageWrapper";
import {
  Alert,
  AlertDescription,
  Box,
  Button,
  ButtonGroup,
  Card,
  Flex,
  HStack,
  Stack,
  StackSeparator,
  Text,
} from "@chakra-ui/react";
import { t } from "i18next";
import { appRoutes } from "../../../../App";
import { AccountView } from "../../models/AccountView";
import { useStores } from "../../../shared/stores/RootStore";
import { AddIcon } from "../../../shared/views/icons";
import { toast } from "../../../shared/views/CustomToaster";

const i18Prefix = "account:Projects";

const AccountProjectsPageBase: React.FC = () => {
  const accountId = useParams().accountId!;
  const { accountStore, authStore } = useStores();

  useEffect(() => {
    accountStore.fetchAccountProjects(accountId);
    // eslint-disable-next-line
  }, [accountId]);

  const addProject = async (projectId: string) => {
    await accountStore
      .addAccountAccess(projectId)
      .then(() => {
        toast("success", t(`${i18Prefix}.addToast.accessGiven`));
      })
      .catch(() => {
        toast("error", t(`${i18Prefix}.addToast.accessFailed`));
      });
  };

  return (
    <AccountPageWrapper
      selectedView={AccountView.Projects}
      accountId={accountId}
    >
      <Text mx={6} fontSize="2xl">
        {t(`${i18Prefix}.tab`)}
      </Text>

      {authStore.isSuperAdmin && (
        <Card.Root mx={6} overflow={"hidden"} variant={"outline"}>
          <Alert.Root status="info">
            <Alert.Indicator />
            <AlertDescription>{t(`${i18Prefix}.superAdmin`)}</AlertDescription>
          </Alert.Root>
        </Card.Root>
      )}

      <Box mx={6}>
        <Stack gap={0} separator={<StackSeparator />}>
          {accountStore.projects.map((project, i) => (
            <HStack key={i} alignItems="stretch">
              <Flex
                asChild
                flexGrow={1}
                px={2}
                _hover={{ bgColor: "gray.50" }}
                alignItems={"center"}
              >
                <Link to={appRoutes.project(project.id)}>
                  <Text>
                    {`${project.city}: `}
                    <strong>{project.name}</strong>
                  </Text>
                </Link>
              </Flex>

              <ButtonGroup variant="ghost" attached size="sm" fontSize={"sm"}>
                <Button
                  onClick={() => addProject(project.id!)}
                  disabled={
                    project.permissions?.canRead && !authStore.isSuperAdmin
                  }
                >
                  <AddIcon /> {t(`${i18Prefix}.add`)}
                </Button>
              </ButtonGroup>
            </HStack>
          ))}
        </Stack>
      </Box>
    </AccountPageWrapper>
  );
};

export const AccountProjectsPage = observer(AccountProjectsPageBase);
